import { useEffect, useRef } from "react";

// Lightweight animated sound-wave lines for dark backgrounds.
// White opacities + subtle vinotinto accents only. pointer-events:none, DPR-capped, rAF.
export const SoundWaves = ({ className = "", lines = 9 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w = 0, h = 0, dpr = 1;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = Math.max(1, w * dpr);
      canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const rows = Array.from({ length: lines }, (_, i) => ({
      speed: 0.00005 + Math.random() * 0.00007,
      phase: Math.random(),
      count: 1 + (i % 2),
      accent: i % 4 === 1,
      amp: 5 + (i % 3) * 2.5,
    }));

    const spindle = (cx, y, bw, amp, fill) => {
      ctx.beginPath();
      ctx.moveTo(cx - bw, y);
      ctx.quadraticCurveTo(cx - bw * 0.4, y - amp, cx, y);
      ctx.quadraticCurveTo(cx + bw * 0.4, y + amp, cx + bw, y);
      ctx.quadraticCurveTo(cx + bw * 0.4, y - amp, cx, y);
      ctx.quadraticCurveTo(cx - bw * 0.4, y + amp, cx - bw, y);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
    };

    const render = (t) => {
      ctx.clearRect(0, 0, w, h);
      const pad = h * 0.1;
      const gap = lines > 1 ? (h - pad * 2) / (lines - 1) : 0;
      const bw = Math.max(40, w * 0.11);
      rows.forEach((r, i) => {
        const y = pad + i * gap;
        ctx.beginPath();
        ctx.moveTo(w * 0.06, y);
        ctx.lineTo(w * 0.94, y);
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.lineWidth = 1;
        ctx.stroke();
        for (let k = 0; k < r.count; k++) {
          const prog = ((t * r.speed + r.phase + k * 0.5) % 1 + 1) % 1;
          const cx = w * 0.09 + prog * (w * 0.82);
          spindle(cx, y, bw, r.amp, r.accent ? "rgba(158,27,34,0.6)" : "rgba(255,255,255,0.72)");
        }
      });
      if (!reduce) raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [lines]);

  return <canvas ref={ref} className={className} style={{ pointerEvents: "none" }} aria-hidden="true" />;
};
